import { useState, useMemo } from 'react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import { Search, ChevronDown } from 'lucide-react';

// Use all countries from the library
const ALLOWED_COUNTRIES = getCountries();

// Country flag emoji helper
const getFlagEmoji = (countryCode) => {
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
};

export default function PhoneInputWithSearch({ value, onChange, defaultCountry = 'TZ' }) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
    const [phoneNumber, setPhoneNumber] = useState('');

    const countries = useMemo(() => {
        return ALLOWED_COUNTRIES.map(code => ({
            code,
            name: en[code] || code,
            callingCode: getCountryCallingCode(code)
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    const filteredCountries = useMemo(() => {
        if (!search) return countries;
        const searchLower = search.toLowerCase();
        return countries.filter(c => 
            c.name.toLowerCase().includes(searchLower) ||
            c.code.toLowerCase().includes(searchLower) ||
            c.callingCode.includes(search)
        );
    }, [countries, search]);

    const selectedCountryData = countries.find(c => c.code === selectedCountry);

    const handleCountrySelect = (countryCode) => {
        setSelectedCountry(countryCode);
        setIsOpen(false);
        setSearch('');
        // Update the full phone number
        const country = countries.find(c => c.code === countryCode);
        if (country && phoneNumber) {
            onChange(`+${country.callingCode}${phoneNumber}`);
        }
    };

    const handlePhoneChange = (e) => {
        const num = e.target.value.replace(/\D/g, '');
        setPhoneNumber(num);
        if (selectedCountryData) {
            onChange(`+${selectedCountryData.callingCode}${num}`);
        }
    };

    return (
        <div className="flex gap-2">
            {/* Country Selector */}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 px-3 py-3 bg-white border border-gray-300 rounded-lg hover:border-[#073763] transition-colors min-w-[100px]"
                >
                    <span className="text-xl">{getFlagEmoji(selectedCountry)}</span>
                    <span className="text-sm text-gray-600">+{selectedCountryData?.callingCode}</span>
                    <ChevronDown size={16} className="text-gray-400" />
                </button>

                {/* Dropdown */}
                {isOpen && (
                    <>
                        <div 
                            className="fixed inset-0 z-40" 
                            onClick={() => { setIsOpen(false); setSearch(''); }}
                        />
                        <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                            {/* Search */}
                            <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        placeholder="Search countries..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#073763]"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            
                            {/* Countries List */}
                            <div className="max-h-60 overflow-y-auto">
                                {filteredCountries.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                        No countries found
                                    </div>
                                ) : (
                                    filteredCountries.map((country) => (
                                        <button
                                            key={country.code}
                                            type="button"
                                            onClick={() => handleCountrySelect(country.code)}
                                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                                                selectedCountry === country.code ? 'bg-[#073763]/10' : ''
                                            }`}
                                        >
                                            <span className="text-xl">{getFlagEmoji(country.code)}</span>
                                            <span className="flex-1 text-sm text-gray-700">{country.name}</span>
                                            <span className="text-sm text-gray-400">+{country.callingCode}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Phone Number Input */}
            <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="Phone number"
                className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg focus:border-[#073763] focus:ring-2 focus:ring-[#073763]/20 outline-none transition-all text-[15px]"
            />
        </div>
    );
}
